import React, { useState } from 'react';
import { Card, Table, DatePicker, Row, Col, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import dayjs from 'dayjs';
import { useRefreshOwnerSnapshots } from '@/hooks/useOwnerIntelligence';
import { useNavigate } from 'react-router-dom';
import { PrimaryKpiCard, OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiAreaTrendChart } from '@/components/owner-intelligence/charts/OiCharts';

const ExecutiveMemoryPage: React.FC = () => {
  const navigate = useNavigate();
  const refresh = useRefreshOwnerSnapshots();
  const [since, setSince] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [preset, setPreset] = useState('7d');

  const applyPreset = (p: string) => {
    setPreset(p);
    if (p === '1d') setSince(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
    else if (p === '7d') setSince(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    else if (p === '30d') setSince(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
    else setSince(dayjs().subtract(90, 'day').format('YYYY-MM-DD'));
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['owner-intelligence', 'change-since', since],
    queryFn: () => ownerIntelligenceService.getChangeSince(since),
  });

  const { data: audit } = useQuery({
    queryKey: ['owner-intelligence', 'audit-log'],
    queryFn: () => ownerIntelligenceService.listAuditLog(),
  });

  const mem = data as {
    delta?: number;
    to?: { composite_score: number; score_date: string };
    rows?: Array<{ score_date: string; composite_score: number; composite_status: string }>;
    open_actions?: number;
    open_alerts?: number;
  };

  const historyChart = (mem?.rows ?? []).map((r) => ({
    label: r.score_date,
    score: r.composite_score,
  }));

  return (
    <OiPageFrame
      subtitle="What changed since yesterday, last week, or last board review — health score memory and audit trail."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Executive memory unavailable"
    >
      <Card style={{ marginBottom: 16 }} bordered={false}>
        <Row gutter={16} align="middle">
          <Col>
            <Select
              value={preset}
              onChange={applyPreset}
              options={[
                { value: '1d', label: 'Since yesterday' },
                { value: '7d', label: 'Since last week' },
                { value: '30d', label: 'Since last month' },
                { value: '90d', label: 'Since last quarter' },
              ]}
            />
          </Col>
          <Col>
            <DatePicker value={dayjs(since)} onChange={(d) => d && setSince(d.format('YYYY-MM-DD'))} />
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <PrimaryKpiCard label="Health delta" value={`${mem?.delta ?? 0} pts`} />
        </Col>
        <Col xs={12} sm={6}>
          <PrimaryKpiCard label="Score now" value={`${mem?.to?.composite_score ?? '—'}%`} />
        </Col>
        <Col xs={12} sm={6}>
          <PrimaryKpiCard
            label="Open actions"
            value={mem?.open_actions ?? 0}
            onClick={() => navigate('/owner-intelligence/actions?tab=actions')}
          />
        </Col>
        <Col xs={12} sm={6}>
          <PrimaryKpiCard
            label="Open alerts"
            value={mem?.open_alerts ?? 0}
            onClick={() => navigate('/owner-intelligence/actions?tab=alerts')}
          />
        </Col>
      </Row>

      <Card title="Health score history" style={{ marginBottom: 16 }} bordered={false}>
        {(mem?.rows ?? []).length === 0 ? (
          <OiEmptyState
            title="No health score history"
            body="Refresh data to generate your first snapshot."
            actionLabel="Refresh data"
            onAction={() => refresh.mutate()}
          />
        ) : (
          <>
            <OiAreaTrendChart data={historyChart} dataKey="score" xKey="label" height={200} />
            <Table
              size="small"
              rowKey="score_date"
              style={{ marginTop: 16 }}
              dataSource={mem?.rows ?? []}
              columns={[
                { title: 'Date', dataIndex: 'score_date' },
                { title: 'Score', dataIndex: 'composite_score' },
                { title: 'Status', dataIndex: 'composite_status' },
              ]}
            />
          </>
        )}
      </Card>

      <Card title="Governance audit log (recent)" bordered={false}>
        <Table
          size="small"
          rowKey="id"
          dataSource={(audit as Record<string, unknown>[]) ?? []}
          locale={{
            emptyText: (
              <OiEmptyState title="No audit entries" body="Governance actions will appear here as they occur." />
            ),
          }}
          columns={[
            { title: 'When', dataIndex: 'created_at' },
            { title: 'Action', dataIndex: 'action_type' },
            { title: 'Entity', dataIndex: 'entity_type' },
          ]}
        />
      </Card>
    </OiPageFrame>
  );
};

export default ExecutiveMemoryPage;
